import { apiClient } from '@api/apiClient';
import loadsureLogo from '@assets/loadsure.svg';
import { Button, Checkbox, CircularProgress } from '@atgfr8/atg-ui-tailwind';
import { BasicModal } from '@components';
import { HoverPop, HoverPopInline } from '@components/HoverPop';
import { ToastType } from '@components/Toast';
import { createForm } from '@felte/solid';
import { closeModal, openModal } from '@store/modals/store';
import {
  commodityCodes,
  customerStore,
  equipmentTypes,
  loadSureStore,
  orderStore,
  setLoadSureStore,
  setOrderStore,
} from '@store/orders';
import { userStore } from '@store/user';
import {
  CheckboxMarkedIcon,
  CheckboxBlankOutlineIcon,
  ShieldCheckIcon,
  InformationIcon,
  SecurityIcon,
} from '@atgfr8/icons';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';
import { formatNumberWithCommas, handleToast } from '@utils/utils';
import { createSignal, onCleanup, onMount } from 'solid-js';
import { produce } from 'solid-js/store';
import { Show } from 'solid-js/web';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import { ErrorPill } from './ErrorPill';
import { InsuranceSkeleton } from './InsuranceSkeleton';
import {
  LoadSureAcknowledgmentModal,
  emptyAcknowledgments,
} from './LoadSureAcknowledgmentModal';
import {
  getLoadSureEligibilityRejection,
  getLoadSurePurchaseGate,
  type LoadSureRejection,
} from './loadSureEligibility';
import { LoadSureEligibilityModal } from './LoadSureEligibilityModal';
import {
  LoadSureExclusionsLink,
  LoadSureTermsLink,
} from './LoadSureLegalLinks';
import {
  InsuranceFormSchema,
  LoadSureCertificate,
  LoadSurePurchaseResponse,
  getAccountManagerEmail,
  hasCustomer,
  hasValidPickupAndDropoff,
  insuranceProduct,
  loadId,
  loadSureAcknowledgmentModal,
  loadSureEligibilityModal,
  stops,
} from './LoadSureHelpers';
import { LoadSureSelect } from './LoadSureSelect';
import { LoadSureTextInput } from './LoadSureTextInput';

type LoadSureQuoteResponse = {
  value: {
    allRiskInsuranceQuote: {
      quoteToken: string;
      insuranceProduct: insuranceProduct;
    };
    gapInsuranceQuote: {
      quoteToken: string;
      insuranceProduct: insuranceProduct;
    };
  };
};

export const InsuranceModal = () => {
  const [origin, setOrigin] = createSignal<LastUsedTopStopModel | null>(null);
  const [destination, setDestination] =
    createSignal<LastUsedTopStopModel | null>(null);
  const [quoteRecieved, setQuoteRecieved] = createSignal<boolean>(false);
  const [loadingQuote, setLoadingQuote] = createSignal<boolean>(false);
  const [loadingPurchase, setLoadingPurchase] = createSignal<boolean>(false);
  const [showOC, setShowOC] = createSignal<boolean>(false);
  const [showOE, setShowOE] = createSignal<boolean>(false);
  const [eligibilityRejection, setEligibilityRejection] =
    createSignal<LoadSureRejection | null>(null);
  const [acknowledgments, setAcknowledgments] = createSignal<boolean[]>(
    emptyAcknowledgments(),
  );

  const { form, data, setData } = createForm<InsuranceFormSchema>({
    initialValues: {
      cargoValue: null,
      equipment: '',
      commodity: '',
      otherCommodity: '',
      otherEquipment: '',
      insuredCompanyName: '',
      insuredCompanyPhone: '',
      insuredCompanyAddress1: '',
      insuredCompanyCity: '',
      insuredCompanyState: '',
      insuredCompanyZip: '',
      insuredCompanyCountry: '',
      customerContactEmail: '',
      descriptionOfGoodsInsured: '',
      confirmation: false,
      isGap: false,
      isAllRisk: false,
    },
  });

  onMount(async () => {
    const originInd = stops().findIndex((s) => s.stopOrder === 0);
    const destinationInd = stops().findIndex(
      (s) => s.stopOrder === stops().length - 1,
    );
    setOrigin(stops()[originInd] ?? null);
    setDestination(stops()[destinationInd] ?? null);
    setData(
      'cargoValue',
      orderStore.order.loads[orderStore.activeTab.index]?.freightValue ?? null,
    );
    //Set cache values if load hasn't changed/user tabbed
    if (loadSureStore.loadId === loadId()) {
      setData('equipment', loadSureStore.equipment);
      setData('commodity', loadSureStore.commodity);
      setData('isGap', loadSureStore.isGap);
      setData('isAllRisk', loadSureStore.isAllRisk);
      setData('otherCommodity', loadSureStore.otherCommodity);
      setData('otherEquipment', loadSureStore.otherEquipment);
      if (data().cargoValue === null) {
        setData('cargoValue', loadSureStore.cargoValue ?? null);
      }
    }

    setData('insuredCompanyName', customerStore.customer.name);
    setData(
      'insuredCompanyPhone',
      orderStore.order.customerContact?.phone1 ?? '',
    );
    setData(
      'insuredCompanyAddress1',
      orderStore.order.customer?.mailingAddress1 ?? '',
    );
    setData('insuredCompanyCity', orderStore.order.customer?.mailingCity ?? '');
    setData(
      'insuredCompanyState',
      orderStore.order.customer?.mailingState ?? '',
    );
    setData('insuredCompanyZip', orderStore.order.customer?.mailingZip ?? '');
    setData('insuredCompanyCountry', 'USA');
    if (Boolean(loadSureStore.otherCommodity)) {
      setShowOC(true);
    }
    if (Boolean(loadSureStore.otherEquipment)) {
      setShowOE(true);
    }
    setData(
      'customerContactEmail',
      await getAccountManagerEmail(orderStore.order.assignedId),
    );
  });

  //This is for cache
  onCleanup(() => {
    setLoadSureStore({
      ...data(),
      loadId: loadId(),
    });
    closeModal(loadSureEligibilityModal);
    closeModal(loadSureAcknowledgmentModal);
  });

  const selectedCommodityId = () => {
    const raw = data().commodity;

    if (raw === null || raw.trim() === '') {
      return null;
    }

    const parsed = Number(raw);

    return Number.isInteger(parsed) ? parsed : null;
  };

  const handleGetQuote = async () => {
    setLoadingQuote(true);
    const payload = {
      loadNum: orderStore.order.loads[orderStore.activeTab.index]?.loadNum,
      cargoValue: Number(data().cargoValue),
      equipment: data().equipment,
      commodity: data().commodity,
      otherCommodity: data().otherCommodity,
      otherEquipment: data().otherEquipment,
      descriptionOfGoodsInsured: data().descriptionOfGoodsInsured,
      userContactEmail: userStore.user.email,
      customerContactEmail: data().customerContactEmail,
      createdByUserId: userStore.user.id,
      loadMode: orderStore.order.loads[orderStore.activeTab.index].mode,
      customer: {
        name: data().insuredCompanyName,
        mailingAddress1: data().insuredCompanyAddress1,
        mailingCity: data().insuredCompanyCity,
        mailingState: data().insuredCompanyState,
        mailingZip: data().insuredCompanyZip,
        mailingCountryCode: data().insuredCompanyCountry,
      },
      stops: stops().map((stop) => ({
        stopDateTime: stop.stopDateTime,
        address1: stop.address1,
        city: stop.city,
        state: stop.state,
        zip: stop.zip,
        origin: stop.id === origin()?.id,
        destination: stop.id === destination()?.id,
        countryCode: stop.countryCode,
        stopOrder: stop.stopOrder,
      })),
    };
    try {
      const trackingConfig = buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
      ]);

      const response: LoadSureQuoteResponse = await apiClient.post(
        '/LoadSure/RequestQuotes',
        payload,
        {
          ...trackingConfig,
          timeout: 120_000,
        },
      );

      setEligibilityRejection(null);
      setLoadSureStore({
        allRiskInsuranceQuote:
          response.value.allRiskInsuranceQuote.insuranceProduct,
        gapInsuranceQuote: response.value.gapInsuranceQuote.insuranceProduct,
        allRiskToken: response.value.allRiskInsuranceQuote.quoteToken,
        gapToken: response.value.gapInsuranceQuote.quoteToken,
        cargoValue: Number(data().cargoValue),
        equipment:
          equipmentTypes.find((c) => c.id.toString() === data().equipment)
            ?.name ?? '',
        commodity:
          commodityCodes.find((c) => c.id.toString() === data().commodity)
            ?.name ?? '',
        insuredCompanyName: data().insuredCompanyName,
      });
      setQuoteRecieved(true);
      setLoadingQuote(false);
      handleToast(ToastType.Success, 'Quote received');
    } catch (error: unknown) {
      setQuoteRecieved(false);
      setLoadingQuote(false);
      const rejection = getLoadSureEligibilityRejection(error);
      const hasClaimedFailure =
        rejection.carrierFailures.length > 0 ||
        rejection.secureRateConFailure !== null;
      setEligibilityRejection(hasClaimedFailure ? rejection : null);
      if (Array.isArray(error)) {
        error.forEach((err) => {
          const { errorMessage } = err as { errorMessage: string };
          const isClaimed =
            rejection.carrierFailures.includes(errorMessage) ||
            rejection.secureRateConFailure === errorMessage;
          if (!isClaimed) {
            handleToast(ToastType.Error, errorMessage);
          }
        });
      }
    }
  };

  const handlePurchase = async () => {
    let payload: {
      QuoteToken: string;
      OrderId: number;
      LoadId: number;
      InsuranceType: string;
      DeclaredValue: number;
      LoadMode: string;
      Equipment: string;
      Commodity: string;
      CreatedByUserId: number;
      InsuredCompanyName: string;
    };
    if (data().isAllRisk) {
      payload = {
        QuoteToken: loadSureStore.allRiskToken ?? '',
        OrderId: orderStore.order.id,
        LoadId: loadId(),
        InsuranceType: 'AllRisk',
        DeclaredValue: loadSureStore.cargoValue ?? 0,
        LoadMode: orderStore.order.loads[orderStore.activeTab.index].mode ?? '',
        Equipment: loadSureStore.equipment ?? '',
        Commodity: loadSureStore.commodity ?? '',
        CreatedByUserId: userStore.user.id,
        InsuredCompanyName: loadSureStore.insuredCompanyName ?? '',
      };
    } else {
      payload = {
        QuoteToken: loadSureStore.gapToken ?? '',
        OrderId: orderStore.order.id,
        LoadId: loadId(),
        InsuranceType: 'Gap',
        DeclaredValue: loadSureStore.cargoValue ?? 0,
        LoadMode: orderStore.order.loads[orderStore.activeTab.index].mode ?? '',
        Equipment: loadSureStore.equipment ?? '',
        Commodity: loadSureStore.commodity ?? '',
        CreatedByUserId: userStore.user.id,
        InsuredCompanyName: loadSureStore.insuredCompanyName ?? '',
      };
    }
    try {
      setLoadingPurchase(true);
      const trackingConfig = buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
      ]);
      const response: {
        value: {
          vendorContract: VendorContractViewModel;
          loadSurePurchaseResponse: LoadSurePurchaseResponse;
          loadSureCertificate: LoadSureCertificate;
        };
      } = await apiClient.post('/LoadSure/PurchaseQuote', payload, {
        ...trackingConfig,
        timeout: 120_000,
      });
      setOrderStore(
        'order',
        'vendors',
        produce((prev) => {
          prev ??= [];
          prev.push(response.value.vendorContract);
          return prev;
        }),
      );
      setOrderStore(
        'order',
        'loads',
        orderStore.activeTab.index,
        'loadSureVendorContractId',
        response.value.vendorContract.id ?? null,
      );
      setOrderStore(
        'order',
        'loads',
        orderStore.activeTab.index,
        'loadSureCertificate',
        response.value.loadSureCertificate,
      );
      handleToast(ToastType.Success, 'Insurance purchased successfully!');
      setLoadingPurchase(false);
      closeModal('insuranceModal');
    } catch (error: unknown) {
      if (Array.isArray(error)) {
        error.forEach((err) => {
          handleToast(
            ToastType.Error,
            (err as { errorMessage: string }).errorMessage,
          );
        });
      } else {
        handleToast(ToastType.Error, 'Failed to purchase insurance');
      }
      setLoadingPurchase(false);
    }
  };

  const isRateSelected = () => data().isGap || data().isAllRisk;

  const selectedQuote = () => {
    if (data().isAllRisk) {
      return loadSureStore.allRiskInsuranceQuote;
    }

    if (data().isGap) {
      return loadSureStore.gapInsuranceQuote;
    }

    return null;
  };

  const stylingHelper = () => {
    if (!isRateSelected()) {
      return 'text-gray-500';
    }
    return 'text-black';
  };

  const resetQuotes = () => {
    setData('isGap', false);
    setData('isAllRisk', false);
    setData('confirmation', false);
    setAcknowledgments(emptyAcknowledgments());
    setEligibilityRejection(null);
    setQuoteRecieved(false);
    closeModal(loadSureEligibilityModal);
    closeModal(loadSureAcknowledgmentModal);
    setLoadSureStore({
      allRiskInsuranceQuote: null,
      gapInsuranceQuote: null,
      isGap: false,
      isAllRisk: false,
    });
  };

  const purchaseGate = () =>
    getLoadSurePurchaseGate(selectedCommodityId(), eligibilityRejection());

  const isPurchaseBlocked = () => {
    const kind = purchaseGate().kind;
    return kind === 'ineligible' || kind === 'secureRateConRequired';
  };

  const needsAcknowledgment = () =>
    purchaseGate().kind === 'acknowledgmentRequired';

  const eligibilityFailures = () => {
    const gate = purchaseGate();
    return gate.kind === 'ineligible' ? gate.failures : [];
  };

  const secureRateConMessage = () => {
    const gate = purchaseGate();
    return gate.kind === 'secureRateConRequired' ? gate.message : null;
  };

  const isTermsCheckboxDisabled = () =>
    !isRateSelected() || isPurchaseBlocked();

  const isConfirmDisabled = () =>
    !isRateSelected() || !data().confirmation || isPurchaseBlocked();

  const selectRate = (rate: 'allRisk' | 'gap') => {
    setData('isAllRisk', rate === 'allRisk' ? !data().isAllRisk : false);
    setData('isGap', rate === 'gap' ? !data().isGap : false);
    setData('confirmation', false);
    setAcknowledgments(emptyAcknowledgments());
  };

  const handleConfirmationChange = (checked: boolean) => {
    if (checked && needsAcknowledgment()) {
      openModal(loadSureAcknowledgmentModal);
      return;
    }
    if (!checked) {
      setAcknowledgments(emptyAcknowledgments());
    }
    setData('confirmation', checked);
  };

  const handleAcknowledgmentsChange = (next: boolean[]) => {
    setAcknowledgments(next);
    if (!next.every((acknowledged) => acknowledged)) {
      setData('confirmation', false);
    }
  };

  const handleAcknowledgmentContinue = () => {
    setData('confirmation', true);
    closeModal(loadSureAcknowledgmentModal);
  };

  return (
    <div>
      <form ref={form}>
        <div class="text-lg font-semibold">Get Insurance</div>

        <div class="grid grid-cols-2 gap-x-2 ">
          <div>
            <div class="bg-gray-100 p-2 rounded-md mb-2">
              <div class="text-xs text-armstrong-primary font-semibold">
                Cargo Value
              </div>
              <div>
                {Boolean(data().cargoValue)
                  ? `$${formatNumberWithCommas(data().cargoValue)}`
                  : 'Please add a cargo value to the load'}
              </div>
            </div>
            <div class="flex flex-col">
              <LoadSureSelect
                label="Equipment"
                value={data().equipment}
                onChange={(e: { target: { value: string } }) => {
                  setData('equipment', e.target.value);
                  resetQuotes();
                  const selected = equipmentTypes.find(
                    (c) => c.id === Number(e.target.value),
                  );
                  if (Boolean(selected?.name.toLowerCase().includes('other'))) {
                    setShowOE(true);
                  } else {
                    setShowOE(false);
                  }
                }}
                required
                options={equipmentTypes.map((c) => ({
                  id: c.id,
                  name: c.name,
                }))}
              />
              <Show when={showOE()}>
                <LoadSureTextInput
                  label="Other Equipment"
                  value={data().otherEquipment}
                  required={true}
                  type="text"
                  onChange={(e: { target: { value: string } }) => {
                    setData('otherEquipment', e.target.value);
                    resetQuotes();
                  }}
                />
              </Show>
              <LoadSureSelect
                label="Commodity"
                value={data().commodity}
                onChange={(e: { target: { value: string } }) => {
                  setData('commodity', e.target.value);
                  const selected = commodityCodes.find(
                    (c) => c.id === Number(e.target.value),
                  );
                  if (Boolean(selected?.name.toLowerCase().includes('misc'))) {
                    setShowOC(true);
                  } else {
                    setShowOC(false);
                  }
                  resetQuotes();
                }}
                required={true}
                options={commodityCodes.map((c) => ({
                  id: c.id,
                  name: c.name,
                }))}
              />
              <Show when={showOC()}>
                <LoadSureTextInput
                  label="Other Commodity"
                  value={data().otherCommodity}
                  required={true}
                  type="text"
                  onChange={(e: { target: { value: string } }) => {
                    setData('otherCommodity', e.target.value);
                    resetQuotes();
                  }}
                />
              </Show>
            </div>
          </div>
          <div>
            <div class="my-2 bg-gray-100 p-3 rounded-md max-h-[500px]">
              <div class="text-md font-semibold">Stops</div>
              <div class="text-sm text-gray-600 mb-2">
                Stop locations are used for quote calculation.
              </div>
              <div class="flex gap-2">
                <div class="flex-1">
                  <div class="text-xs text-armstrong-primary font-semibold mb-1 ">
                    Origin
                  </div>
                  <div class="w-full text-black">
                    {origin()
                      ? `${origin()?.address1}, ${origin()?.city} ${
                          origin()?.state
                        } ${origin()?.zip}`
                      : 'Please add stops to get a quote'}
                  </div>
                </div>
                <div class="flex-1">
                  <div class="text-xs text-armstrong-primary font-semibold mb-1">
                    Destination
                  </div>
                  <div class="w-full text-black">
                    {destination()
                      ? `${destination()?.address1}, ${
                          destination()?.city
                        } ${destination()?.state} ${destination()?.zip}`
                      : 'Please add stops to get a quote'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="h-full col-span-2">
            <div class="relative my-2">
              <label class="absolute -top-2 left-2 bg-white px-1 text-sm font-medium text-gray-700">
                Description of Goods Insured
              </label>
              <textarea
                rows={3}
                class="w-full px-3 py-2 border-2 border-[#80b6cf] rounded-md shadow-xs focus:outline-hidden focus:border-[#123b50] hover:border-[#123b50] resize-y"
                value={data().descriptionOfGoodsInsured ?? ''}
                onChange={(e: { target: { value: string } }) => {
                  setData('descriptionOfGoodsInsured', e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div class="w-full border-t border-gray-200 my-2"></div>
        <div class="text-lg font-semibold my-2">
          Insured Company Information
        </div>
        <div class="grid grid-cols-2 gap-x-2">
          <LoadSureTextInput
            label="Insured Company Email"
            value={data().customerContactEmail}
            onChange={(e: { target: { value: string } }) => {
              setData('customerContactEmail', e.target.value);
            }}
            required={true}
            type="text"
          />
          <LoadSureTextInput
            label="Insured Company Name"
            value={data().insuredCompanyName}
            onChange={(e: { target: { value: string } }) => {
              setData('insuredCompanyName', e.target.value);
            }}
            required={true}
            type="text"
          />
          <div class="col-span-2">
            <LoadSureTextInput
              label="Insured Company Address"
              value={data().insuredCompanyAddress1}
              onChange={(e: { target: { value: string } }) => {
                setData('insuredCompanyAddress1', e.target.value);
              }}
              required={true}
              type="text"
            />
          </div>
          <div class="flex gap-x-2">
            <div class="w-1/2">
              <LoadSureTextInput
                label="Insured Company City"
                value={data().insuredCompanyCity}
                onChange={(e: { target: { value: string } }) => {
                  setData('insuredCompanyCity', e.target.value);
                }}
                required={true}
                type="text"
              />
            </div>
            <div class=" w-1/2">
              <LoadSureTextInput
                label="Insured Company State"
                value={data().insuredCompanyState}
                onChange={(e: { target: { value: string } }) => {
                  setData('insuredCompanyState', e.target.value);
                }}
                required={true}
                type="text"
              />
            </div>
          </div>
          <div class="flex gap-x-2">
            <div class="w-1/2">
              <LoadSureTextInput
                label="Insured Company Country"
                value={data().insuredCompanyCountry}
                onChange={(e: { target: { value: string } }) => {
                  setData('insuredCompanyCountry', e.target.value);
                }}
                required={true}
                type="text"
              />
            </div>
            <div class=" w-1/2">
              <LoadSureTextInput
                label="Insured Company Zip"
                value={data().insuredCompanyZip}
                onChange={(e: { target: { value: string } }) => {
                  setData('insuredCompanyZip', e.target.value);
                }}
                required={true}
                type="text"
              />
            </div>
          </div>
        </div>
        <Show when={!hasCustomer() || !hasValidPickupAndDropoff()}>
          <div class="flex gap-4 my-2">
            <div class="underline text-md py-2 font-semibold">
              Fix the following issues to initiate a quote request:
            </div>
            <div class="flex gap-2">
              <Show when={!hasCustomer()}>
                <ErrorPill text="Order needs a customer" />
              </Show>
              <Show when={!hasValidPickupAndDropoff()}>
                <ErrorPill text="Load needs at least two stops" />
              </Show>
            </div>
          </div>
        </Show>
        <div class="w-full my-2">
          <Button
            onClick={handleGetQuote}
            variant="contained"
            startIcon={<SecurityIcon />}
            color="info"
            sx={{
              width: '100%',
            }}
            endIcon={loadingQuote() ? <CircularProgress size={20} /> : null}
            disabled={
              loadingQuote() ||
              !hasCustomer() ||
              !hasValidPickupAndDropoff() ||
              !Boolean(data().equipment) ||
              !Boolean(data().commodity) ||
              !Boolean(data().cargoValue) ||
              (!Boolean(data().otherCommodity) && showOC()) ||
              (!Boolean(data().otherEquipment) && showOE())
            }
            dataTestId="get-insurance-rates-button"
          >
            Get Rates
          </Button>
        </div>
        <div class="w-full flex justify-center">
          <img
            src={loadsureLogo}
            alt="LoadSure Logo"
            class="w-1/2 [clip-path:inset(5%_30%_5%_30%)]"
          />
        </div>
        <Show when={quoteRecieved()}>
          <div class="bg-[#e6f1f6] p-3 rounded-md my-2">
            <span class="font-semibold text-base tracking-tight">
              Statement of Fact:
            </span>
            {` ${loadSureStore.gapInsuranceQuote?.statementOfFact ?? ''}`}
          </div>
        </Show>
        <Show when={quoteRecieved()}>
          <div class="bg-[#ffecb3] p-3 rounded-md my-2 text-armstrong-primary font-semibold">
            This information can change as you modify your coverage selections.
          </div>
        </Show>
        <div class="grid grid-cols-2 gap-x-2">
          <Show
            when={quoteRecieved()}
            fallback={<InsuranceSkeleton label="All-Risk Insurance" />}
          >
            <div class="border-2 border-gray-300 rounded-lg shadow-xs bg-[#fffbf6] px-4 py-2">
              <div class="flex items-center gap-2">
                <ShieldCheckIcon color="#026EA1" />
                <div class="text-lg font-semibold">All-Risk Insurance</div>
                <div class="bg-[#E6F4EA] px-2 py-1 text-[#248670] font-semibold rounded-xl text-xs">
                  RECOMMENDED
                </div>
                <div class="ml-auto">
                  <HoverPop
                    anchor={<InformationIcon color="#026EA1" />}
                    render={true}
                  >
                    <div>
                      <div class="bg-[#616161] text-white text-xs p-2  max-w-[600px]">
                        All-Risk Insurance provides full coverage for the entire
                        value of the load, with a standard deductible.
                        Recommended when you need full protection, regardless of
                        the motor carrier's coverage limits.
                      </div>
                      <div class="bg-[#616161] text-white text-xs p-2  max-w-[600px] font-semibold">
                        Why we recommend purchasing All-Risk Insurance?
                      </div>
                      <div class="bg-[#616161] text-white text-xs p-2  max-w-[600px]">
                        While Armstrong provides a Blanket All-Risk Insurance
                        policy for loads up to $1,000,000, this policy carries a
                        significant deductible - typically between $25,000 and
                        $50,000 - if a claim occurs. Purchasing All-Risk
                        Insurance through Loadsure is strongly recommended to
                        help reduce your potential out-of-pocket costs and
                        provide an extra layer of protection for your load and
                        your customer.
                      </div>
                    </div>
                  </HoverPop>
                </div>
              </div>
              <div class="text-sm text-armstrong-primary font-semibold">
                Full load coverage, standard deductible
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Limit: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.allRiskInsuranceQuote?.limit,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Deductible: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.allRiskInsuranceQuote?.deductible,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Premium: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.allRiskInsuranceQuote?.premium,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Taxes & Fees: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.allRiskInsuranceQuote?.tax,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1 font-semibold text-armstrong-blue">
                <div>Price: </div>
                <div class="text-lg">
                  $
                  {formatNumberWithCommas(
                    (loadSureStore.allRiskInsuranceQuote?.premium ?? 0) +
                      (loadSureStore.allRiskInsuranceQuote?.tax ?? 0),
                  )}
                </div>
              </div>
              <div class="my-2">
                For complete details, see{' '}
                <HoverPopInline
                  anchor={
                    <div class="text-armstrong-blue underline ">Exclusions</div>
                  }
                  render={true}
                >
                  <div class="text-sm p-4  max-w-[600px]">
                    <Show
                      fallback={<div>No exclusions</div>}
                      when={Boolean(
                        loadSureStore.allRiskInsuranceQuote?.commodityExclusions
                          .length,
                      )}
                    >
                      <div>
                        <div class="text-lg font-semibold mb-2">
                          List of excluded commodities
                        </div>
                        {loadSureStore.allRiskInsuranceQuote?.commodityExclusions.map(
                          (exclusion) => (
                            <div class="capitalize">
                              <span class="font-semibold text-sm">•</span>{' '}
                              {exclusion}
                            </div>
                          ),
                        )}
                      </div>
                    </Show>
                  </div>
                </HoverPopInline>
                {' and '}
                <a
                  href={
                    loadSureStore.allRiskInsuranceQuote?.termsAndConditionsLink
                  }
                  target="_blank"
                  class="text-armstrong-blue underline"
                >
                  Terms and Conditions
                </a>
              </div>
              <Button
                onClick={() => selectRate('allRisk')}
                variant="contained"
                class="w-full"
                startIcon={
                  data().isAllRisk ? (
                    <CheckboxMarkedIcon size={20} />
                  ) : (
                    <CheckboxBlankOutlineIcon size={20} />
                  )
                }
                color="info"
                disabled={isPurchaseBlocked()}
                dataTestId="select-all-risk-insurance-button"
              >
                SELECT INSURANCE RATE
              </Button>
            </div>
          </Show>
          <Show
            when={quoteRecieved()}
            fallback={<InsuranceSkeleton label="GAP Insurance" />}
          >
            <div class="border-2 border-gray-300 rounded-lg shadow-xs bg-[#fffbf6] px-4 py-2">
              <div class="flex items-center gap-2">
                <ShieldCheckIcon color="#026EA1" />
                <div class="text-lg font-semibold">GAP Insurance</div>
                <div class="ml-auto">
                  <HoverPop
                    anchor={<InformationIcon color="#026EA1" />}
                    render={true}
                  >
                    <div class="bg-[#616161] text-white text-xs p-2  max-w-[600px]">
                      GAP Insurance covers the difference between the value of
                      the load and the carrier's cargo insurance coverage. It
                      has a higher deductible ($100K) and is typically used when
                      the carrier has partial coverage.
                    </div>
                  </HoverPop>
                </div>
              </div>
              <div class="text-sm text-armstrong-primary font-semibold">
                Top-up coverage, $100K deductible
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Limit: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.gapInsuranceQuote?.limit,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Deductible: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.gapInsuranceQuote?.deductible,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Premium: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(
                    loadSureStore.gapInsuranceQuote?.premium,
                  )}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1">
                <div>Taxes & Fees: </div>
                <div class="text-lg font-semibold">
                  $
                  {formatNumberWithCommas(loadSureStore.gapInsuranceQuote?.tax)}
                </div>
              </div>
              <div class="flex items-center justify-between flex-wrap my-1 font-semibold text-armstrong-blue">
                <div>Price: </div>
                <div class="text-lg">
                  $
                  {formatNumberWithCommas(
                    (loadSureStore.gapInsuranceQuote?.premium ?? 0) +
                      (loadSureStore.gapInsuranceQuote?.tax ?? 0),
                  )}
                </div>
              </div>
              <div class="my-2">
                For complete details, see{' '}
                <HoverPopInline
                  anchor={
                    <div class="text-armstrong-blue underline ">Exclusions</div>
                  }
                  render={true}
                >
                  <div class="text-sm p-4  max-w-[600px]">
                    <Show
                      fallback={<div>No exclusions</div>}
                      when={Boolean(
                        loadSureStore.gapInsuranceQuote?.commodityExclusions
                          .length,
                      )}
                    >
                      <div>
                        <div class="text-lg font-semibold mb-2">
                          List of excluded commodities
                        </div>
                        {loadSureStore.gapInsuranceQuote?.commodityExclusions.map(
                          (exclusion) => (
                            <div class="capitalize text-sm">
                              <span class="font-semibold">•</span> {exclusion}
                            </div>
                          ),
                        )}
                      </div>
                    </Show>
                  </div>
                </HoverPopInline>
                {' and '}
                <a
                  href={loadSureStore.gapInsuranceQuote?.termsAndConditionsLink}
                  target="_blank"
                  class="text-armstrong-blue underline"
                >
                  Terms and Conditions
                </a>
              </div>
              <Button
                onClick={() => selectRate('gap')}
                variant="contained"
                class="w-full"
                startIcon={
                  data().isGap ? (
                    <CheckboxMarkedIcon size={20} />
                  ) : (
                    <CheckboxBlankOutlineIcon size={20} />
                  )
                }
                color="info"
                disabled={isPurchaseBlocked()}
                dataTestId="select-gap-insurance-button"
              >
                SELECT INSURANCE RATE
              </Button>
            </div>
          </Show>
        </div>
        <Show when={eligibilityFailures().length > 0}>
          <div class="bg-[#FBE9E7] border border-[#F2C1BA] rounded-md px-4 py-3 my-2 text-sm text-[#8D001A]">
            Carrier is not eligible to purchase Loadsure insurance for the
            selected commodity because one or more eligibility requirements were
            not met. Please{' '}
            <span class="font-semibold">remove the carrier from the load</span>{' '}
            and select an eligible carrier to continue.{' '}
            <button
              type="button"
              class="underline font-semibold cursor-pointer"
              onClick={() => openModal(loadSureEligibilityModal)}
              data-testid="loadsure-view-eligibility-failures-link"
            >
              Click here
            </button>{' '}
            to review the failed requirements.
          </div>
        </Show>
        <Show when={secureRateConMessage()}>
          {(message) => (
            <div
              class="bg-[#FBE9E7] border border-[#F2C1BA] rounded-md px-4 py-3 my-2 text-sm text-[#8D001A]"
              data-testid="loadsure-secure-ratecon-banner"
            >
              {message()}
            </div>
          )}
        </Show>
        <Show when={quoteRecieved()}>
          <div class="flex items-center">
            <Checkbox
              checked={data().confirmation}
              disabled={isTermsCheckboxDisabled()}
              color="info"
              class="ml-[-9px]"
              onChange={handleConfirmationChange}
              dataTestId="confirm-insurance-terms-checkbox"
            />
            <div class={stylingHelper()}>
              I confirm that I have{' '}
              <Show
                when={needsAcknowledgment() && !isTermsCheckboxDisabled()}
                fallback="read"
              >
                <button
                  type="button"
                  class="text-armstrong-blue underline cursor-pointer"
                  onClick={() => openModal(loadSureAcknowledgmentModal)}
                  data-testid="loadsure-open-acknowledgment-link"
                >
                  read
                </button>
              </Show>{' '}
              and understand the{' '}
              <LoadSureExclusionsLink quote={selectedQuote()} /> and{' '}
              <LoadSureTermsLink
                quote={selectedQuote()}
                label="Terms and Conditions"
              />{' '}
              of this insurance.
            </div>
          </div>
        </Show>
        <div class="my-2 flex gap-2 justify-end">
          <Button
            variant="contained"
            onClick={handlePurchase}
            color="info"
            disabled={isConfirmDisabled()}
            endIcon={loadingPurchase() ? <CircularProgress size={20} /> : null}
            dataTestId="confirm-insurance-purchase-button"
          >
            Confirm
          </Button>
          <Button
            onClick={() => closeModal('insuranceModal')}
            dataTestId="cancel-insurance-modal-button"
          >
            Cancel
          </Button>
        </div>
      </form>
      <BasicModal
        id={loadSureEligibilityModal}
        width="97vw"
        maxWidth="600px"
        header={false}
        footer={false}
        hideClose={true}
        mainContainerStyles={{ padding: '0' }}
      >
        <LoadSureEligibilityModal failures={eligibilityFailures()} />
      </BasicModal>
      <BasicModal
        id={loadSureAcknowledgmentModal}
        width="97vw"
        maxWidth="600px"
        header={false}
        footer={false}
        hideClose={true}
        mainContainerStyles={{ padding: '0' }}
      >
        <LoadSureAcknowledgmentModal
          acknowledgments={acknowledgments()}
          selectedQuote={selectedQuote()}
          onAcknowledgmentsChange={handleAcknowledgmentsChange}
          onContinue={handleAcknowledgmentContinue}
        />
      </BasicModal>
    </div>
  );
};
